import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getBlogBySlug, getTranslation, ContentDocument } from '../api/contentApi';
import BlogContent from '../components/BlogContent';

const BlogDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const currentLang = lang || 'de';

  const [post, setPost] = useState<ContentDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    getBlogBySlug(slug)
      .then((data) => {
        if (!data) {
          setNotFound(true);
        } else {
          setPost(data);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container"><p>{t('common.loading')}</p></div>;

  if (notFound || !post) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">404</h1>
        <p className="text-gray-600 mb-6">
          {currentLang === 'de' ? 'Beitrag nicht gefunden.' : currentLang === 'fr' ? 'Article introuvable.' : 'Post not found.'}
        </p>
        <Link to={`/${currentLang}/blog`} className="btn">{t('common.backToHome')}</Link>
      </div>
    );
  }

  const tr = getTranslation(post, currentLang);
  if (!tr) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <p className="text-gray-600">
          {currentLang === 'de' ? 'Dieser Beitrag ist in Ihrer Sprache nicht verfügbar.' : currentLang === 'fr' ? 'Cet article n\'est pas disponible dans votre langue.' : 'This post is not available in your language.'}
        </p>
        <Link to={`/${currentLang}/blog`} className="btn mt-4 inline-block">{t('nav.blog')}</Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{tr.seoTitle || tr.title || 'Blog'} — CLAIRMONT | Rechtsanwalt Marcel Küster</title>
        {tr.seoDescription && <meta name="description" content={tr.seoDescription} />}
        <html lang={currentLang} />
      </Helmet>

      {/* Hero section — 100vh full bleed; uses post featured image when available, default avif otherwise */}
      <section
        className="hero-section"
        style={{
          backgroundImage: `url(${post.featuredImageUrl || `${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif`})`
        }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{tr.title}</h1>
        </div>
      </section>

      <article className="max-w-4xl mx-auto py-12 px-4">
        {/* Back link */}
        <Link to={`/${currentLang}/blog`} className="text-primary-500 hover:text-primary-700 text-sm mb-6 inline-block">
          &larr; {t('nav.blog')}
        </Link>

        {/* Title */}
        <h1 className="text-4xl font-serif font-bold text-primary-900 mb-4">{tr.title}</h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-8 border-b border-gray-200 pb-4">
          {post.publicationDate && (
            <span>{t('common.publishedOn')} {new Date(post.publicationDate).toLocaleDateString(currentLang)}</span>
          )}
          {post.author && <span>{t('common.by')} <Link to={`/${currentLang}/portrait`} className="hover:underline">{post.author}</Link></span>}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs bg-primary-50 text-primary-700 px-3 py-1 rounded-full font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        <BlogContent html={tr.content || ''} lang={currentLang} className="prose prose-lg max-w-none" />

        {/* Disclaimer */}
        {tr.disclaimer && (
          <aside className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-500">
            <p className="font-semibold text-gray-700 mb-2">{t('blog.disclaimerTitle')}</p>
            <div className="prose prose-sm max-w-none text-gray-500" dangerouslySetInnerHTML={{ __html: tr.disclaimer }} />
            <Link
              to={`/${currentLang}/impressum#disclaimer`}
              className="inline-block mt-3 text-primary-500 hover:text-primary-700 underline text-xs"
            >
              {t('blog.disclaimerLinkText')}
            </Link>
          </aside>
        )}
      </article>
    </>
  );
};

export default BlogDetailPage;
